import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TITLES = [
  "Evening at a Popular Cafe",
  "Dinner at an Expensive Restaurant",
  "Brunch at a Premium Hotel",
  "Afternoon Tea at a Heritage Spot",
  "Social Walk at a Cultural Landmark",
];

const labelCls = "text-xs uppercase tracking-widest text-[#A0A0A0]";
const inputCls =
  "bg-[#15151A] border-white/10 text-white placeholder:text-[#5a5a66] focus:border-[#D4AF37] focus-visible:ring-[#D4AF37]/50";

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

export default function CreatePlan() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("19:30");
  const [bill, setBill] = useState("");
  const [saving, setSaving] = useState(false);

  const billNum = parseInt(bill, 10) || 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!title) return toast.error("Choose one of the five curated experiences");
    if (!neighborhood.trim()) return toast.error("Neighborhood is required");
    if (!date || !time) return toast.error("Pick a date and time");
    if (!billNum || billNum < 1000) return toast.error("Minimum estimated bill is ₹1,000");

    const [hh, mm] = time.split(":").map(Number);
    const dt = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hh, mm);
    if (dt <= new Date()) return toast.error("Plan must be in the future");

    setSaving(true);
    try {
      await api.post("/plans", {
        title,
        neighborhood: neighborhood.trim(),
        datetime: dt.toISOString(),
        estimated_bill: billNum,
      });
      toast.success("Table reserved — pay your 40% deposit to go live");
      navigate("/my-plans");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create plan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <div data-testid="create-plan-page">
        <h1 className="font-serif text-3xl font-bold tracking-tight text-white mb-1">Sponsor a Table</h1>
        <p className="text-sm text-[#A0A0A0] mb-8">You pay the bill at the venue. The Circle brings the company.</p>

        <form onSubmit={submit} className="space-y-6 fade-up">
          <div className="space-y-2">
            <Label className={labelCls}>Experience</Label>
            <Select value={title} onValueChange={setTitle}>
              <SelectTrigger data-testid="plan-title-select" className={inputCls}>
                <SelectValue placeholder="Choose an experience" />
              </SelectTrigger>
              <SelectContent className="bg-[#15151A] border-[#D4AF37]/25 text-white">
                {TITLES.map((t) => (
                  <SelectItem key={t} value={t} data-testid={`plan-title-option-${TITLES.indexOf(t)}`}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className={labelCls}>Neighborhood</Label>
            <Input
              data-testid="plan-neighborhood-input"
              value={neighborhood}
              onChange={(e) => setNeighborhood(e.target.value)}
              placeholder="e.g., Park Street, Salt Lake"
              className={inputCls}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelCls}>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="plan-date-trigger"
                    className={`w-full justify-start font-normal ${inputCls} hover:bg-[#1A1A24] hover:text-white`}
                  >
                    <CalendarIcon className="w-4 h-4 mr-2 text-[#D4AF37]" strokeWidth={1.5} />
                    {date ? format(date, "d MMM yyyy") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#15151A] border-[#D4AF37]/25" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                    data-testid="plan-date-calendar"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Time</Label>
              <Input
                data-testid="plan-time-input"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className={labelCls}>Estimated Bill (INR)</Label>
            <Input
              data-testid="plan-bill-input"
              type="number"
              min="1000"
              value={bill}
              onChange={(e) => setBill(e.target.value)}
              placeholder="Minimum ₹1,000"
              className={inputCls}
            />
          </div>

          {billNum >= 1000 && (
            <div
              className="rounded-lg border border-[#D4AF37]/20 bg-[#15151A] px-4 py-3 flex justify-between text-xs"
              data-testid="plan-deposit-preview"
            >
              <span className="text-[#A0A0A0]">
                Your deposit <span className="text-white">(40%)</span>
                <span className="block text-[#D4AF37] text-sm font-semibold mt-0.5">{inr(Math.round(billNum * 0.4))}</span>
                <span className="block text-[10px] mt-0.5">via Razorpay — goes live instantly</span>
              </span>
              <span className="text-[#A0A0A0] text-right">
                Joinee deposit <span className="text-white">(25%)</span>
                <span className="block text-[#D4AF37] text-sm font-semibold mt-0.5">{inr(Math.round(billNum * 0.25))}</span>
              </span>
            </div>
          )}

          <Button
            type="submit"
            disabled={saving}
            data-testid="plan-submit-button"
            className="w-full h-12 bg-[#D4AF37] text-[#0B0B0F] hover:bg-[#F1D570] font-semibold rounded-md text-base transition-colors"
          >
            {saving ? "Opening your table..." : "Open the Table"}
          </Button>
        </form>
      </div>
    </AppShell>
  );
}
